import type { Category } from 'TYPES/api.body';

export type LandingCategorySlot = {
  key: string;
  label: string;
  image: string;
  fallbackId: number;
  matches: (name: string) => boolean;
};

/** madina.ng MVP — always three landing tiles. */
export const LANDING_CATEGORY_SLOTS: LandingCategorySlot[] = [
  {
    key: 'groceries',
    label: 'Groceries',
    image: '/images/categories/groceries.png',
    fallbackId: 38,
    matches: name => /grocery|groceries|market|supermarket/i.test(name)
  },
  {
    key: 'pharmacy',
    label: 'Pharmacy',
    image: '/images/categories/pharmacy.png',
    fallbackId: 39,
    matches: name => /pharmacy|pharma|medical|health|medicine/i.test(name)
  },
  {
    key: 'electronics',
    label: 'Electrical Accessories',
    image: '/images/categories/electronics.png',
    fallbackId: 40,
    matches: name => /electronics|electrical|accessories|tech|gadget|appliance/i.test(name)
  }
];

export type ResolvedLandingCategory = {
  id: number;
  name: string;
  /** Primary image (API logo when valid, else MVP asset path). */
  image: string;
  /** Static MVP tile used when API logo fails to load. */
  fallbackImage: string;
  slug: string;
};

const erpAssetBase = (): string =>
  (process.env.REACT_APP_BASE_URL ?? process.env.REACT_BASE_URL ?? '').replace(/\/$/, '');

/** Turn Tier-1 / legacy category logos into browser-loadable URLs. */
export const normalizeCategoryLogoUrl = (logo?: string | null): string | undefined => {
  if (logo == null) return undefined;
  const trimmed = logo.trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  const base = erpAssetBase();
  if (trimmed.startsWith('/')) return base ? `${base}${trimmed}` : trimmed;
  return base ? `${base}/${trimmed.replace(/^\//, '')}` : trimmed;
};

export const getCategoryFallbackImage = (categoryName: string): string => {
  const name = categoryName.trim().toLowerCase();

  if (/grocery|groceries|market|supermarket/.test(name)) {
    return '/images/categories/groceries.png';
  }
  if (/pharmacy|pharma|medical|health|medicine/.test(name)) {
    return '/images/categories/pharmacy.png';
  }
  if (/electronics|electrical|accessories|tech|gadget|appliance/.test(name)) {
    return '/images/categories/electronics.png';
  }

  return '/images/categories/default.png';
};

export const makeCategorySlug = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Map API categories onto the three fixed landing slots (Tier-1 or legacy). */
export const resolveLandingCategories = (categories: Category[] = []): ResolvedLandingCategory[] => {
  const storefront = categories.filter(category => !/sexual|betacare/i.test(category.name));

  return LANDING_CATEGORY_SLOTS.flatMap((slot): ResolvedLandingCategory[] => {
    const exact = storefront.find(category => category.name.trim().toLowerCase() === slot.label.toLowerCase());
    const match = exact ?? storefront.find(category => slot.matches(category.name));
    if (!match && storefront.length > 0) return [];

    const displayName = match?.name ?? slot.label;
    const fallbackImage = slot.image || getCategoryFallbackImage(displayName);
    const apiLogo = normalizeCategoryLogoUrl(match?.logo);

    return [
      {
        id: match?.id ?? slot.fallbackId,
        name: displayName,
        image: apiLogo ?? fallbackImage,
        fallbackImage,
        slug: makeCategorySlug(displayName)
      }
    ];
  });
};
